"use client";

import { PropsWithTitle } from "@/types/ui";
import { useIsMobile } from "@hooks/use-mobile";
import React, { Dispatch, DispatchWithoutAction, FC, PropsWithChildren, ReactNode, startTransition, useCallback, useEffect, useState } from "react";
import { useTranslations } from "next-intl";
import { AlertDialog, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@components/ui/alert-dialog";
import { Button } from "@components/ui/button";
import { Drawer, DrawerClose, DrawerContent, DrawerDescription, DrawerFooter, DrawerHeader, DrawerPortal, DrawerTitle, DrawerTrigger } from "@components/ui/drawer";
import { cn } from "@lib/utils";
interface AlertDialogWrapperProps extends PropsWithChildren, PropsWithTitle {
  trigger?: React.JSX.Element;
  closeTitle?: string;
  handlers?: {
    onClose?: DispatchWithoutAction;
    onAction?: () => Promise<void>;
  };
  actionTitle?: string;
  actionDisabled?: boolean;
  closeDisabled?: boolean;
  hideCloseButton?: boolean;
  interceptEscapeWhenCloseDisabled?: boolean;
  setIsOpenCallback?: Dispatch<boolean> | undefined;
  footerNode?: ReactNode;
  onEscapeKeyDown?: React.ComponentProps<typeof AlertDialogContent>["onEscapeKeyDown"];
  focusCloseButtonOnMobile?: boolean;
  initialFocusRef?: React.RefObject<HTMLElement | null>;
  isOpen?: boolean;
  classNames?: {
    description?: string;
    content?: string;
    body?: string;
    header?: string;
    title?: string;
    footer?: string;
  };
}

/**
 * AlertDialogWrapper - Optimized dialog/drawer wrapper with lazy content loading
 *
 * Performance: Only mounts children when the dialog is actually opened
 */
export const AlertDialogWrapper: FC<AlertDialogWrapperProps> = ({
  trigger,
  title,
  description,
  children,
  closeTitle,
  actionTitle,
  handlers,
  actionDisabled,
  closeDisabled,
  hideCloseButton,
  interceptEscapeWhenCloseDisabled,
  setIsOpenCallback,
  footerNode,
  onEscapeKeyDown,
  focusCloseButtonOnMobile,
  initialFocusRef,
  classNames,
  ...props
}: AlertDialogWrapperProps) => {
  // DEPS
  const {
    onClose,
    onAction
  } = handlers ?? {};
  const isMobile = useIsMobile();
  const tCommon = useTranslations("common");

  // STATE
  const [isOpen, setIsOpen] = useState(props.isOpen === undefined ? false : props.isOpen);
  const closeButtonRef = React.useRef<HTMLButtonElement>(null);

  // HANDLERS
  const onOpenChange = useCallback((open: boolean) => {
    if (!open && closeDisabled) {
      return;
    }
    if (!open) onClose?.();
    if (setIsOpenCallback) {
      setIsOpenCallback(open);
    }
    setIsOpen(open);
  }, [closeDisabled, onClose, setIsOpenCallback]);
  const onEscapeKeyDownHandler = useCallback<NonNullable<typeof onEscapeKeyDown>>(event => {
    if (closeDisabled && !interceptEscapeWhenCloseDisabled) {
      event.preventDefault();
      return;
    }
    onEscapeKeyDown?.(event);
    if (closeDisabled) event.preventDefault();
  }, [closeDisabled, interceptEscapeWhenCloseDisabled, onEscapeKeyDown]);
  const onActionHandler = async () => {
    try {
      await onAction?.();
      setIsOpen(false);
    } catch {
      // Keep the dialog open. The action owner is responsible for user-facing error state.
    }
  };
  useEffect(() => {
    if (props.isOpen === undefined) {
      return;
    }
    startTransition(() => {
      onOpenChange(props.isOpen as boolean);
    });
  }, [onOpenChange, props.isOpen]);
  if (isMobile) {
    return <Drawer dismissible={!closeDisabled} onOpenChange={onOpenChange} open={isOpen}>
        {trigger && <DrawerTrigger asChild>{trigger}</DrawerTrigger>}
        {/* Lazy load: only mount portal and content when the drawer is actually open */}
        {isOpen && <DrawerPortal>
            <DrawerContent className={cn("bg-card", classNames?.content)} overlayClassName="fixed inset-0 bg-black/40" onEscapeKeyDown={onEscapeKeyDownHandler} onOpenAutoFocus={event_0 => {
          if (initialFocusRef) {
            event_0.preventDefault();
            initialFocusRef.current?.focus({
              preventScroll: true
            });
            return;
          }
          if (!focusCloseButtonOnMobile) return;
          event_0.preventDefault();
          closeButtonRef.current?.focus({
            preventScroll: true
          });
        }}>
              <DrawerHeader className={cn("text-left", classNames?.header)}>
                <DrawerTitle className={classNames?.title}>{title}</DrawerTitle>
              </DrawerHeader>
              {description && <DrawerDescription className={cn("px-2 pb-2 text-center text-sm text-muted-foreground md:text-left", classNames?.description)}>
                  {description}
                </DrawerDescription>}
              <div className={cn("flex flex-col gap-2 overflow-y-auto px-2 pb-2", classNames?.body)}>
                {children}
              </div>
              <DrawerFooter className={cn("border-t pt-2", classNames?.footer)}>
                {footerNode && <div className="mr-auto">{footerNode}</div>}
                {!hideCloseButton && <DrawerClose asChild>
                    <Button ref={closeButtonRef} variant="outline" disabled={closeDisabled} onClick={onClose}>
                      {closeTitle ?? tCommon("words.verbs.close")}
                    </Button>
                  </DrawerClose>}
                {onAction && <Button onClick={onActionHandler} disabled={actionDisabled} type="button">
                    {actionTitle ?? tCommon("words.verbs.continue")}
                  </Button>}
              </DrawerFooter>
            </DrawerContent>
          </DrawerPortal>}
      </Drawer>;
  }
  return <AlertDialog onOpenChange={onOpenChange} open={isOpen}>
      {trigger && <AlertDialogTrigger asChild>{trigger}</AlertDialogTrigger>}
      {/* Lazy load: only mount content when the dialog is actually open */}
      {isOpen && <AlertDialogContent onEscapeKeyDown={onEscapeKeyDownHandler} onOpenAutoFocus={event_1 => {
      if (!initialFocusRef) return;
      event_1.preventDefault();
      initialFocusRef.current?.focus({
        preventScroll: true
      });
    }} className={cn("max-h-[95vh] overflow-hidden bg-card px-1 py-4 sm:max-w-5/6 lg:max-w-2/3 xl:max-w-fit", classNames?.content)}>
          <div className={cn("flex max-h-[85vh] flex-col gap-2 overflow-y-auto px-3 pb-2", classNames?.body)}>
            <AlertDialogHeader className={classNames?.header}>
              <AlertDialogTitle className={classNames?.title}>{title}</AlertDialogTitle>
            </AlertDialogHeader>
            <AlertDialogDescription className={classNames?.description}>
              {description}
            </AlertDialogDescription>
            {children}
          </div>
          <AlertDialogFooter className={cn("flex items-center gap-2 border-t px-3 pt-3", classNames?.footer)}>
            {footerNode && <div className="mr-auto">{footerNode}</div>}
            {!hideCloseButton && <AlertDialogCancel disabled={closeDisabled}>
                {closeTitle ?? tCommon("words.verbs.close")}
              </AlertDialogCancel>}
            {onAction && <Button onClick={onActionHandler} disabled={actionDisabled} type="button">
                {actionTitle ?? tCommon("words.verbs.continue")}
              </Button>}
          </AlertDialogFooter>
        </AlertDialogContent>}
    </AlertDialog>;
};